import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { usePmt3ProblemsDashboardData } from '@/hooks/usePmt3ProblemsDashboardData';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { DocumentData } from 'firebase/firestore';
import { fetchLatestOldValuesMap } from '@/services/pmt3OldValuesService';

// --- Formatting Helpers ---
const parseSafeFloat = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const cleanedValue = String(value).replace(/[$,%]/g, '').replace(/,/g, '');
    const num = parseFloat(cleanedValue);
    return isNaN(num) ? null : num;
};

// Parses percentage strings (e.g., "20%") or decimals (e.g., 0.20) into a decimal
const parsePercentAsDecimal = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null;
    let num: number | null = null;
    const stringValue = String(value);
    if (stringValue.includes('%')) {
        num = parseSafeFloat(stringValue); // Removes % and parses
        if (num !== null) {
            num = num / 100; // Convert whole number percentage to decimal
        }
    } else {
        // Assume it's already a decimal if no % sign
        num = parseSafeFloat(stringValue);
    }
    return num;
}

const formatCurrency = (value: any, decimals = 0): string => {
    const num = parseSafeFloat(value);
    if (num === null) return '-';
    // Use 2 decimals for CPC
    if (decimals === 2) {
         return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
};

// Correctly formats a decimal (e.g., 0.25) into a rounded percentage string (e.g., "25%")
const formatDecimalAsPercent = (value: any): string => {
    const num = parseSafeFloat(value);
    if (num === null) return '-';
    return `${Math.round(num * 100)}%`;
};

// Formats a number directly as a percentage (e.g., 25 becomes "25%")
const formatDirectPercent = (value: any): string => {
    const num = parseSafeFloat(value);
    if (num === null) return '-';
    return `${Math.round(num)}%`;
}

const formatNumber = (value: any): string => {
    const num = parseSafeFloat(value);
    if (num === null) return '-';
    return num.toLocaleString('en-US');
};

const formatDefault = (value: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
};
// Format a value by type (used for old values from the ledger)
const formatValue = (value: any, formatType: FormatType): string => {
    switch (formatType) {
        case 'currency': return formatCurrency(value, 0);
        case 'currency2dec': return formatCurrency(value, 2);
        case 'percent': return formatDecimalAsPercent(value);
        case 'percent_direct': return formatDirectPercent(value);
        case 'number': return formatNumber(value);
        default: return formatDefault(value);
    }
};
// --- End Formatting Helpers ---


// Dashboard Item Configuration
type FormatType = 'currency' | 'percent' | 'number' | 'default' | 'currency2dec' | 'percent_direct';
interface DashboardItemConfig {
    label: string;
    field: string | null; // Firestore field name (use exact case)
    format: FormatType;
}

// Use exact Firestore field names and CORRECTED format types
const allDashboardItems: DashboardItemConfig[] = [
    { label: '28 Day PPC Imp', field: '28 Day PPC Imp', format: 'number' },
    { label: '28 Day PPC Clicks', field: '28 Day PPC Clicks', format: 'number' },
    { label: '28 Day PPC Spend', field: '28 Day PPC Spend', format: 'currency' },
    { label: '28 Day PPC Sales', field: '28 Day PPC Sales', format: 'currency' },
    { label: '28 Day PPC Orders', field: '28 Day PPC Orders', format: 'number' },
    { label: '28 Day PPC Units', field: '28 Day PPC Units', format: 'number' }, // Conditional Style applied later
    // ** Use 'percent' format for fields stored as decimals **
    { label: '28 Day PPC CTR', field: '28 Day PPC CTR', format: 'percent' },
    // ** Use 'percent' format for fields stored as decimals **
    { label: '28 Day PPC CR', field: '28 Day PPC CR', format: 'percent' },
    { label: '28 Day PPC CPC', field: '28 Day PPC CPC', format: 'currency2dec' }, // Use 2 decimals
    // ** Use 'percent' format for fields stored as decimals **
    { label: '28 Day PPC ACoS', field: '28 Day PPC ACoS', format: 'percent' },
    // ** Use 'percent' format for fields stored as decimals **
    { label: 'Final Target ACoS', field: 'Final Target ACoS', format: 'percent' },
    // ACoS Difference is calculated client-side, format directly
    { label: 'ACoS Difference', field: null, format: 'percent_direct' }, // Keep direct format for calculated diff
    // 28 Day PPC Profit is calculated client-side
    { label: '28 Day PPC Profit', field: null, format: 'currency' }, // Conditional Style applied later
];

// Split items for two columns
const midPoint = Math.ceil(allDashboardItems.length / 2);
const column1ItemsConfig = allDashboardItems.slice(0, midPoint);
const column2ItemsConfig = allDashboardItems.slice(midPoint);

// Simple Item Renderer - Accepts color, background, icon, and oldValue props
interface ItemDisplayProps {
    label: string;
    value: string;
    isLoading: boolean;
    colorClass?: string;
    bgClass?: string;
    icon?: React.ReactNode;
    oldValue?: string;
}
const ItemDisplay: React.FC<ItemDisplayProps> = ({ label, value, isLoading, colorClass = '', bgClass = '', icon = null, oldValue }) => (
     <TableRow className={bgClass}>
        <TableCell className="font-medium py-2 px-4">{label}</TableCell>
        <TableCell className={`text-right py-2 px-4 ${colorClass}`}>
            {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                <div className="flex items-center justify-end space-x-1">
                    {icon}
                    <span className="font-semibold">{value}</span>
                </div>
            )}
        </TableCell>
        <TableCell className="text-right py-2 px-2">
            {isLoading ? <Skeleton className="h-4 w-12 float-right" /> : (
                oldValue && oldValue !== '-' && oldValue !== value ? (
                    <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100 text-xs">
                        {oldValue}
                    </span>
                ) : null
            )}
        </TableCell>
    </TableRow>
);


type OldValuesMap = Record<string, string>;

const PPCResultsDashboard: React.FC = () => {
    const { selectedProduct } = usePMT3Selection();
    const { data, isLoading, error } = usePmt3ProblemsDashboardData(selectedProduct?.companyName);
    const [oldValuesMap, setOldValuesMap] = useState<OldValuesMap | null>(null);

    // Find the data for the currently selected product
    const currentProductData = data?.productData?.find(p => p.id === selectedProduct?.id);

    // Fetch old values for comparison
    useEffect(() => {
        setOldValuesMap(null);
        if (selectedProduct?.companyName && selectedProduct?.id) {
            fetchLatestOldValuesMap(selectedProduct.companyName, selectedProduct.id)
                .then(setOldValuesMap)
                .catch(() => setOldValuesMap(null));
        }
    }, [selectedProduct]);

    // Helper to get raw and formatted value for an item config
    const getValueAndFormat = (item: DashboardItemConfig, productData: DocumentData | undefined): { formattedValue: string, rawValue: number | null } => {
        let value: any;
        let rawValue: number | null = null;

        // Handle calculated fields first
        if (item.label === 'ACoS Difference') {
            // Use parseDecimalAsPercent to handle potential % signs if needed, though Firestore likely stores decimals
            const acosDecimal = parsePercentAsDecimal(productData?.['28 Day PPC ACoS']);
            const targetAcosDecimal = parsePercentAsDecimal(productData?.['Final Target ACoS']);
            if (acosDecimal !== null && targetAcosDecimal !== null) {
                 // Calculate difference between the percentage numbers
                 rawValue = Math.round(acosDecimal * 100) - Math.round(targetAcosDecimal * 100);
            }
            value = rawValue; // Use raw percentage difference for formatting
        } else if (item.label === '28 Day PPC Profit') {
            const ppcSales = parseSafeFloat(productData?.['28 Day PPC Sales']);
            // Use parsePercentAsDecimal for safety, assuming they are stored as decimals (e.g., 0.15)
            const referralFeeDecimal = parsePercentAsDecimal(productData?.['Referral Fee %']);
            const bufferDecimal = parsePercentAsDecimal(productData?.['Final Gross Profit Buffer %']);
            const ppcUnits = parseSafeFloat(productData?.['28 Day PPC Units']);
            const fbaFee = parseSafeFloat(productData?.['FBA Fee']);
            const cogs = parseSafeFloat(productData?.['COGS']);

            if (ppcSales !== null && referralFeeDecimal !== null && bufferDecimal !== null && ppcUnits !== null && fbaFee !== null && cogs !== null) {
                // Calculate profit: PPC Sales * (1 - Fees) - (Units * Unit Costs)
                rawValue = ppcSales * (1 - referralFeeDecimal - bufferDecimal) - (ppcUnits * (fbaFee + cogs));
            }
            value = rawValue;
        } else if (item.field) {
            // Access direct fields using bracket notation
            value = productData?.[item.field];
            // Get raw numeric value for fields needing conditional styling
             if (['28 Day PPC Units'].includes(item.label)) {
                 rawValue = parseSafeFloat(value);
             }
        } else {
            value = null;
        }

        // Apply formatting based on the item's config
        let formattedValue = '-';
        switch (item.format) {
            case 'currency': formattedValue = formatCurrency(value, 0); break;
            case 'currency2dec': formattedValue = formatCurrency(value, 2); break;
            case 'percent': formattedValue = formatDecimalAsPercent(value); break; // Handles decimals
            case 'percent_direct': formattedValue = formatDirectPercent(value); break; // Handles whole numbers
            case 'number': formattedValue = formatNumber(value); break;
            default: formattedValue = formatDefault(value); break;
        }
         // Ensure rawValue is set if not calculated above but needed for styling
         if (item.label === '28 Day PPC Units' && rawValue === null) {
             rawValue = parseSafeFloat(value);
         }

        return { formattedValue, rawValue };
    };

     // Helper to determine styling based on item label and raw value
    const getItemStyling = (label: string, rawValue: number | null): { colorClass: string, bgClass: string, icon: React.ReactNode } => {
        let colorClass = 'text-gray-900 dark:text-gray-100'; // Default color
        let bgClass = '';
        let icon: React.ReactNode = null;

        switch (label) {
            case '28 Day PPC Units':
                if (rawValue !== null) {
                    if (rawValue > 0) { colorClass = 'text-green-700 dark:text-green-500'; icon = <CheckCircle size={16} className={colorClass} />; }
                    else if (rawValue < 0) { colorClass = 'text-red-600 dark:text-red-500'; bgClass = 'bg-red-50 dark:bg-red-900/20'; icon = <AlertCircle size={16} className={colorClass} />; }
                    else { colorClass = 'text-yellow-500 dark:text-yellow-400'; icon = null; }
                }
                break;
            case 'ACoS Difference':
                 if (rawValue !== null) {
                    if (rawValue < 0) { colorClass = 'text-green-700 dark:text-green-500'; bgClass = 'bg-green-50 dark:bg-green-900/20'; icon = <CheckCircle size={16} className={colorClass} />; }
                    else { colorClass = 'text-red-600 dark:text-red-500'; bgClass = 'bg-red-50 dark:bg-red-900/20'; icon = <AlertCircle size={16} className={colorClass} />; }
                }
                break;
            case '28 Day PPC Profit':
                 if (rawValue !== null) {
                    if (rawValue > 0) { colorClass = 'text-green-700 dark:text-green-500'; bgClass = 'bg-green-50 dark:bg-green-900/20'; icon = <CheckCircle size={16} className={colorClass} />; }
                    else if (rawValue < 0) { colorClass = 'text-red-600 dark:text-red-500'; bgClass = 'bg-red-50 dark:bg-red-900/20'; icon = <AlertCircle size={16} className={colorClass} />; }
                }
                break;
            default:
                break;
        }
        return { colorClass, bgClass, icon };
    };


    if (error) {
        return <Card><CardHeader><CardTitle className="text-2xl font-bold">PPC Results</CardTitle></CardHeader><CardContent><p className="text-red-600 text-sm">Error loading data: {error.message}</p></CardContent></Card>;
    }

     if (!selectedProduct && !isLoading) {
         return (
            <Card>
                <CardHeader><CardTitle className="text-2xl font-bold">PPC Results</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-gray-500">Select a product to view PPC Results.</p></CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="p-4">
                <CardTitle className="text-2xl font-bold">PPC Results</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                     {/* Column 1 */}
                     <Table>
                         <TableBody>
                             {column1ItemsConfig.map((item) => {
                                const { formattedValue, rawValue } = getValueAndFormat(item, currentProductData);
                                const { colorClass, bgClass, icon } = getItemStyling(item.label, rawValue);
                                const oldValue = item.field ? oldValuesMap?.[item.field] : undefined;
                                const formattedOld = oldValue !== undefined ? formatValue(oldValue, item.format) : undefined;
                                return (
                                    <ItemDisplay
                                        key={item.label}
                                        label={item.label}
                                        value={formattedValue}
                                        isLoading={isLoading}
                                        colorClass={colorClass}
                                        bgClass={bgClass}
                                        icon={icon}
                                        oldValue={formattedOld}
                                    />
                                );
                             })}
                         </TableBody>
                     </Table>
                     {/* Column 2 */}
                     <Table>
                         <TableBody>
                             {column2ItemsConfig.map((item) => {
                                const { formattedValue, rawValue } = getValueAndFormat(item, currentProductData);
                                const { colorClass, bgClass, icon } = getItemStyling(item.label, rawValue);
                                const oldValue = item.field ? oldValuesMap?.[item.field] : undefined;
                                const formattedOld = oldValue !== undefined ? formatValue(oldValue, item.format) : undefined;
                                return (
                                    <ItemDisplay
                                        key={item.label}
                                        label={item.label}
                                        value={formattedValue}
                                        isLoading={isLoading}
                                        colorClass={colorClass}
                                        bgClass={bgClass}
                                        icon={icon}
                                        oldValue={formattedOld}
                                    />
                                );
                             })}
                         </TableBody>
                     </Table>
                 </div>
                 {!currentProductData && !isLoading && (
                     <div className="text-center text-sm text-gray-500 py-4">
                         No data found for selected product.
                     </div>
                 )}
            </CardContent>
        </Card>
    );
};

export default PPCResultsDashboard;